import type { TourConfig, TourResolveContext } from '../types/tour.types'

// Auto-collect every tour definition. Dropping a new `tours/<screen>.tour.ts` file
// that default-exports a TourConfig registers it automatically — no edits here.
const modules = import.meta.glob<{ default: TourConfig }>('./tours/*.tour.ts', {
  eager: true,
})

const ALL_TOURS: TourConfig[] = Object.values(modules)
  .map((m) => m.default)
  .filter((t): t is TourConfig => Boolean(t && t.id && t.routeName))
  .sort((a, b) => a.order - b.order)

/** Every registered tour (unfiltered). */
export function allTours(): TourConfig[] {
  return ALL_TOURS
}

/** Is this tour available to the current user (feature flag + admin gating)? */
export function isTourAvailable(tour: TourConfig, ctx: TourResolveContext): boolean {
  if (tour.adminOnly && !ctx.isAdmin) return false
  if (tour.featureFlag && !ctx.isFlagEnabled(tour.featureFlag)) return false
  return true
}

/** Tours available to the current user. */
export function resolveAvailableTours(ctx: TourResolveContext): TourConfig[] {
  return ALL_TOURS.filter((t) => isTourAvailable(t, ctx))
}

function conditionMatches(tour: TourConfig): boolean {
  if (!tour.condition) return false
  try {
    return tour.condition()
  } catch {
    return false
  }
}

/**
 * The tour bound to a route name, if any. When several tours share a route, the
 * one whose `condition()` currently matches wins (state-aware); otherwise the
 * first unconditional tour is used.
 */
export function resolveTourForRoute(
  routeName: string | null | undefined,
  ctx: TourResolveContext,
): TourConfig | null {
  if (!routeName) return null
  const candidates = ALL_TOURS.filter(
    (t) => t.routeName === routeName && isTourAvailable(t, ctx),
  )
  return (
    candidates.find(conditionMatches) ?? candidates.find((t) => !t.condition) ?? null
  )
}

/** All available tours bound to a route (regardless of condition). */
export function toursForRoute(
  routeName: string | null | undefined,
  ctx: TourResolveContext,
): TourConfig[] {
  if (!routeName) return []
  return ALL_TOURS.filter((t) => t.routeName === routeName && isTourAvailable(t, ctx))
}

/** Look up a tour by id (unfiltered — caller decides availability). */
export function getTourById(id: string): TourConfig | null {
  return ALL_TOURS.find((t) => t.id === id) ?? null
}

/** Distinct modules in display order, each with its available tours. */
export function resolveModules(
  ctx: TourResolveContext,
): { module: string; tours: TourConfig[] }[] {
  const available = resolveAvailableTours(ctx)
  const order: string[] = []
  const byModule = new Map<string, TourConfig[]>()
  for (const tour of available) {
    if (!byModule.has(tour.module)) {
      byModule.set(tour.module, [])
      order.push(tour.module)
    }
    byModule.get(tour.module)!.push(tour)
  }
  return order.map((module) => ({ module, tours: byModule.get(module)! }))
}
